import { useEffect } from "react";

import { ArrowLeft, Loader2 } from "lucide-react";

import { useSignalTitle } from "./useSignalTitle";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import type { SignalTier } from "../../Store/Reducers/SignalFeedSlice";
import {
  fetchAccountSignalHistory,
  type AccountSignalHistoryEvent,
} from "../../Store/Reducers/SignalHistorySlice";

interface Props {
  accountId: string;
  accountName?: string;
  onBack: () => void;
}

/** mock `fcPill()` tier styling (ActionCard.tsx's own PILL_STYLE) — kept as
 * a small local copy rather than imported, matching this codebase's
 * existing per-file tier-map convention (e.g. portfolioMapVisualHelpers.ts's
 * TIER_HALO). */
const TIER_PILL: Record<SignalTier, string> = {
  critical: "bg-[rgba(220,38,38,0.12)] text-[#dc2626]",
  warning: "bg-[rgba(217,119,6,0.12)] text-[#b45309]",
  opp: "bg-[rgba(13,148,136,0.12)] text-[#0d9488]",
  maint: "bg-[#f3f4f6] text-[#6b7280]",
};
const TIER_LABEL: Record<SignalTier, string> = {
  critical: "Critical",
  warning: "Warning",
  opp: "Opportunity",
  maint: "Maintenance",
};
const STATE_LABEL: Record<AccountSignalHistoryEvent["state"], string> = {
  active: "Active",
  acknowledged: "Acknowledged",
  snoozed: "Snoozed",
  resolved: "Resolved",
};
const STATE_PILL: Record<AccountSignalHistoryEvent["state"], string> = {
  active: "bg-[rgba(220,38,38,0.12)] text-[#dc2626]",
  acknowledged: "bg-[#f3f4f6] text-[#6b7280]",
  snoozed: "bg-[rgba(217,119,6,0.12)] text-[#b45309]",
  resolved: "bg-[rgba(13,148,136,0.12)] text-[#0d9488]",
};
const INTERACTION_LABEL: Record<string, string> = {
  cta_clicked: "CTA clicked",
  dismissed: "Dismissed",
  snoozed: "Snoozed",
  resolved_after_action: "Resolved after action",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Builds the mock's timeline "Notes" column (signal-service-design-note.md
 * §8.1 — "Resolved 2026-07-14 — AL refreshed", "Recurred (13d)") from the
 * raw firing + interaction rows, since the backend intentionally returns
 * data, not pre-formatted copy (same split as signalCTAs.ts's evidence
 * templating for the live feed). */
function notesFor(event: AccountSignalHistoryEvent): string {
  if (event.state === "resolved" && event.resolved_at) {
    const suffix =
      event.resolution_reason === "condition_cleared:resolved_after_action"
        ? " — resolved after action"
        : "";
    return `Resolved ${formatDate(event.resolved_at)}${suffix}`;
  }
  const last = event.interactions[event.interactions.length - 1];
  if (last?.event_type === "snoozed") {
    const days = (last.metadata as { snoozeDays?: number } | null)?.snoozeDays;
    return `Snoozed${days ? ` ${days}d` : ""} on ${formatDate(last.created_at)}`;
  }
  if (last?.event_type === "dismissed") {
    return `Dismissed ${formatDate(last.created_at)}`;
  }
  return `Still ${STATE_LABEL[event.state].toLowerCase()} since ${formatDate(event.first_fired_at)}`;
}

/** §8.1 Per-Account Signal Timeline — every signal this account has ever
 * had, independent of who owns it ("account memory"). Embedded in place
 * inside Actions → History (SignalHistoryList.tsx) rather than navigated
 * to, so the tab/list context isn't lost — also reused as-is by the
 * standalone /account-signal-history/:accountId route for a shareable
 * direct link (Pages/AccountSignalHistory/index.tsx). */
const SignalHistoryDetail: React.FC<Props> = ({
  accountId,
  accountName,
  onBack,
}) => {
  const signalTitle = useSignalTitle();
  const dispatch = useAppDispatch();
  const { events, eventsLoading, eventsError, historyAvailable } =
    useAppSelector(state => state.signalHistory);

  useEffect(() => {
    void dispatch(fetchAccountSignalHistory(accountId));
  }, [dispatch, accountId]);

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 self-start text-xs font-semibold text-gray-500 hover:text-purple-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Back to accounts
      </button>

      <div>
        <h2 className="text-base font-bold text-gray-900">
          Signal history{accountName ? ` — ${accountName}` : ""}
        </h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Every signal this account has had — active, resolved, or in between.
        </p>
      </div>

      {eventsLoading && events.length === 0 && (
        <div className="flex min-h-[240px] items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span className="ml-2 text-sm">Loading signal history…</span>
        </div>
      )}

      {eventsError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Couldn&apos;t load signal history: {eventsError}
        </div>
      )}

      {!eventsLoading && !eventsError && !historyAvailable && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm font-semibold text-gray-700">
            Signals aren&apos;t turned on for your organization.
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Your administrator controls this setting. Signal history appears
            here once signals are turned on.
          </p>
        </div>
      )}

      {!eventsLoading &&
        !eventsError &&
        historyAvailable &&
        events.length === 0 && (
          <p className="text-xs text-gray-400">
            No signals have ever fired for this account.
          </p>
        )}

      {events.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="grid grid-cols-[100px_1fr_100px_110px_1fr] gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            <span>Date</span>
            <span>Signal</span>
            <span>Tier</span>
            <span>State</span>
            <span>Notes</span>
          </div>
          {events.map(event => (
            <div
              key={event.signal_firing_id}
              className="grid grid-cols-[100px_1fr_100px_110px_1fr] gap-3 border-b border-gray-100 px-3 py-2.5 text-[12px] last:border-b-0"
            >
              <span className="text-gray-500">
                {formatDate(event.first_fired_at)}
              </span>
              <span className="font-semibold text-gray-900">
                {signalTitle(event.signal_key)}
              </span>
              <span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold ${TIER_PILL[event.tier]}`}
                >
                  {TIER_LABEL[event.tier]}
                </span>
              </span>
              <span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold ${STATE_PILL[event.state]}`}
                >
                  {STATE_LABEL[event.state]}
                </span>
              </span>
              <span className="text-gray-500">{notesFor(event)}</span>
            </div>
          ))}
        </div>
      )}

      {events.length > 0 && events.some(e => e.interactions.length > 0) && (
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Interaction detail
          </h3>
          {events
            .filter(e => e.interactions.length > 0)
            .map(event => (
              <div
                key={event.signal_firing_id}
                className="rounded-lg border border-gray-200 bg-gray-50 p-3"
              >
                <p className="text-[12.5px] font-bold text-gray-900">
                  {signalTitle(event.signal_key)}
                </p>
                <div className="mt-1.5 flex flex-col gap-1">
                  {event.interactions.map((interaction, i) => (
                    <p key={i} className="text-[11px] text-gray-500">
                      {formatDate(interaction.created_at)} —{" "}
                      {INTERACTION_LABEL[interaction.event_type] ||
                        interaction.event_type}
                      {interaction.cta_label
                        ? ` (${interaction.cta_label})`
                        : ""}
                    </p>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default SignalHistoryDetail;
